<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ConnectModal from './components/ConnectModal.vue';
import DashboardCard from './components/DashboardCard.vue';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const route = useRoute();
const accountId = route.params.accountId;

const instances = ref([]);
const loading = ref(true);
const selectedInstance = ref(null);
const showConnectModal = ref(false);
const showDeleteModal = ref(false);
const instanceToDelete = ref(null);

const fetchInstances = async () => {
  loading.value = true;
  try {
    const response = await window.axios.get(`/api/v1/accounts/${accountId}/evolution`);
    instances.value = response.data;
  } catch (error) {
    useAlert('Erro ao carregar instâncias');
  } finally {
    loading.value = false;
  }
};

const totalInstances = computed(() => instances.value.length);
const activeInstances = computed(() => instances.value.filter(i => i.connectionStatus === 'open').length);
const inactiveInstances = computed(() => instances.value.filter(i => i.connectionStatus !== 'open').length);

onMounted(() => {
  fetchInstances();
});

const handleConnect = (instance) => {
  selectedInstance.value = instance;
  showConnectModal.value = true;
};

const handleRestart = async (instance) => {
  try {
    await window.axios.post(`/api/v1/accounts/${accountId}/evolution/${instance.name}/restart`);
    useAlert('Comando de reinício enviado!');
    setTimeout(fetchInstances, 2000);
  } catch (error) {
    useAlert('Erro ao reiniciar a conexão');
  }
};

const handleDelete = (instance) => {
  instanceToDelete.value = instance;
  showDeleteModal.value = true;
};

const confirmDelete = async (deleteInbox) => {
  if (!instanceToDelete.value) return;
  
  const instance = instanceToDelete.value;
  showDeleteModal.value = false;
  
  try {
    await window.axios.delete(`/api/v1/accounts/${accountId}/evolution/${instance.name}?keep_inbox=${!deleteInbox}`);
    useAlert('Conexão excluída com sucesso!');
    fetchInstances();
  } catch (error) {
    useAlert('Erro ao excluir a conexão');
  } finally {
    instanceToDelete.value = null;
  }
};

const handleModalClose = () => {
  showConnectModal.value = false;
  selectedInstance.value = null;
  fetchInstances();
};
</script>

<template>
  <div class="p-6 h-full overflow-y-auto">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-n-slate-12">Conexões WhatsApp</h1>
      <p class="text-n-slate-11">Gerencie todas as suas conexões e vincule novos números em um só lugar.</p>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div class="bg-n-slate-2 p-6 rounded-xl border border-n-slate-3">
        <h3 class="text-xs uppercase text-n-slate-11 font-semibold mb-2">Total de Conexões</h3>
        <p class="text-4xl font-bold text-n-slate-12">{{ totalInstances }} <span class="text-sm font-normal text-n-slate-10">aparelhos</span></p>
      </div>
      <div class="bg-n-slate-2 p-6 rounded-xl border border-n-slate-3 border-l-4 border-l-green-500">
        <h3 class="text-xs uppercase text-n-slate-11 font-semibold mb-2">Conectadas</h3>
        <p class="text-4xl font-bold text-green-500">{{ activeInstances }} <span class="text-sm font-normal text-n-slate-10">ativas</span></p>
      </div>
      <div class="bg-n-slate-2 p-6 rounded-xl border border-n-slate-3 border-l-4 border-l-red-500">
        <h3 class="text-xs uppercase text-n-slate-11 font-semibold mb-2">Desconectadas</h3>
        <p class="text-4xl font-bold text-red-500">{{ inactiveInstances }} <span class="text-sm font-normal text-n-slate-10">inativas</span></p>
      </div>
    </div>

    <!-- Instances Grid -->
    <div v-if="loading" class="flex justify-center p-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-slate-12"></div>
    </div>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <DashboardCard
        v-for="instance in instances"
        :key="instance.id"
        :instance="instance"
        @connect="handleConnect(instance)"
        @restart="handleRestart(instance)"
        @delete="handleDelete(instance)"
      />
      <!-- Add new card -->
      <router-link :to="{ name: 'settings_inboxes_page_channel', params: { sub_page: 'evolution' } }" class="bg-n-slate-2 rounded-xl border border-n-slate-3 border-dashed flex flex-col items-center justify-center p-8 hover:bg-n-slate-3 transition-colors cursor-pointer min-h-[250px]">
        <div class="w-12 h-12 rounded-full bg-n-slate-4 flex items-center justify-center mb-4 text-n-slate-11">
          <i class="i-lucide-plus text-xl"></i>
        </div>
        <h3 class="font-bold text-n-slate-12 mb-1">Adicionar Aparelho</h3>
        <p class="text-sm text-n-slate-10 text-center">Vincule um novo número criando uma nova caixa de entrada.</p>
      </router-link>
    </div>

    <ConnectModal
      v-if="showConnectModal"
      :show="showConnectModal"
      :instance="selectedInstance"
      :account-id="accountId"
      @close="handleModalClose"
    />

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity">
      <div class="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-700 transform transition-all">
        <div class="p-6">
          <div class="flex items-center gap-3 mb-4 text-red-500">
            <div class="p-2 bg-red-100 dark:bg-red-500/20 rounded-full">
              <i class="i-lucide-alert-triangle text-xl"></i>
            </div>
            <h3 class="text-xl font-bold text-slate-800 dark:text-white">Excluir Conexão</h3>
          </div>
          
          <p class="text-sm text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
            Você está prestes a desconectar <strong>{{ instanceToDelete?.profileName || instanceToDelete?.name }}</strong>. 
            O que você deseja fazer com o histórico de conversas no Chatwoot?
          </p>
          
          <div class="flex flex-col gap-3">
            <button @click="confirmDelete(true)" class="w-full p-4 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 rounded-xl text-left border border-red-200 dark:border-red-500/30 transition-all group">
              <div class="font-bold text-red-700 dark:text-red-400 group-hover:text-red-800 dark:group-hover:text-red-300 flex justify-between items-center">
                Apagar Tudo (Sem volta)
                <i class="i-lucide-trash-2"></i>
              </div>
              <div class="text-xs text-red-600/80 dark:text-red-400/80 mt-1 leading-relaxed">
                Desconecta o número e <strong>apaga permanentemente</strong> a caixa de entrada, junto com todas as conversas e mídias no Chatwoot.
              </div>
            </button>

            <button @click="confirmDelete(false)" class="w-full p-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl text-left border border-slate-200 dark:border-slate-600 transition-all group">
              <div class="font-bold text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white flex justify-between items-center">
                Apenas Desconectar
                <i class="i-lucide-archive"></i>
              </div>
              <div class="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                A conexão é desfeita, mas a caixa de entrada e as <strong>conversas antigas continuam salvas</strong> no Chatwoot apenas para leitura.
              </div>
            </button>
          </div>
        </div>
        <div class="bg-slate-50 dark:bg-slate-900/50 px-6 py-4 flex justify-end border-t border-slate-100 dark:border-slate-800">
          <button @click="showDeleteModal = false" class="px-5 py-2 rounded-lg font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
